/**
 * Symbiosis Room — Field of the Third.
 *
 * Three-column live negotiation space (Participant A · YUO · Participant B).
 * All UI matches the standalone reference — pure-CSS gradient stones, no
 * icons/glyphs, capsule buttons, drift/breathe/spin/emerge animations.
 *
 * Two views inside this component:
 *   • RoomList  — landing when no share_id in URL
 *   • Room      — when /symbiosis/room/:share_id
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useResponsive } from "@/lib/responsive";
import AutoTextarea from "@/components/yuo/AutoTextarea";

// -------------------------------------------------------------------------
// Design constants
// -------------------------------------------------------------------------
const KEYFRAMES = `
  @keyframes syb-emerge {
    from { opacity: 0; filter: blur(14px); transform: translateY(14px) scale(.985); }
    to   { opacity: 1; filter: blur(0);    transform: none; }
  }
  @keyframes syb-breathe {
    0%, 100% { opacity: .5; }
    50%      { opacity: 1; }
  }
  @keyframes syb-drift {
    0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
    50%      { transform: translate3d(2%, -3%, 0) scale(1.08); }
  }
  @keyframes syb-spin {
    to { transform: rotate(360deg); }
  }
  @keyframes syb-pulse-halo {
    0%, 100% { box-shadow: 0 0 0 0 rgba(var(--role-green-rgb),0.35); }
    50%      { box-shadow: 0 0 0 14px rgba(var(--role-green-rgb),0); }
  }
  .syb-tr-btn { opacity: 0; transition: opacity .25s, color .25s, filter .25s; }
  .syb-tr-btn:hover { box-shadow: none !important; }
  .syb-msg:hover .syb-tr-btn, .syb-tr-btn:focus-visible { opacity: 1; }
  .syb-tr-btn.on { opacity: 1; }
  .syb-tr-btn.busy { opacity: 1; animation: syb-spin .9s linear infinite; }
  @media (hover: none) { .syb-tr-btn { opacity: .4; } .syb-tr-btn.on, .syb-tr-btn.busy { opacity: 1; } }
  .syb-ph, .syb-ph:focus { box-shadow: none !important; border-radius: 0 !important; background: transparent !important; }
  .syb-ph::placeholder { font-size: 9.5px; letter-spacing: .2em; text-transform: uppercase; color: var(--fg-35); }
`;

// Local-only translation store: localStorage "translate_{messageId}_{targetLang}". Never sent to the server.
const trKey = (id, lang) => `translate_${id}_${lang}`;
const trGet = (id, lang) => { try { return localStorage.getItem(trKey(id, lang)); } catch { return null; } };
const trSet = (id, lang, text) => { try { localStorage.setItem(trKey(id, lang), text); } catch {} };
const uiLang = () => navigator.language || "en";

// Function colours (question / answer / advise)
const FN = {
  question: { rgb: "255,122,102", hex: "#FF7A66", text: "var(--role-coral-accent)",  label: "QUESTION" },
  answer:   { rgb: "52,255,155",  hex: "#34FF9B", text: "var(--fn-answer-text)",     label: "ANSWER"   },
  advise:   { rgb: "233,196,106", hex: "#E9C46A", text: "var(--role-yellow-accent)", label: "ADVISE"   },
};

// Role palettes (used ONLY for stones and role pills)
const A_ROLE = {
  rgb: "10,132,255", light_rgb: "180,220,255", light2: "var(--lit-blue-18)",
  stone_grad: "radial-gradient(38% 32% at 34% 26%, rgb(207,230,255) 0%, rgb(79,157,255) 22%, rgb(18,64,127) 58%, rgb(6,11,24) 100%)",
  small_grad: "radial-gradient(36% 32% at 32% 26%, rgb(207,230,255), rgb(79,157,255) 26%, rgb(13,47,92) 70%, rgb(4,7,15) 100%)",
  chip: "var(--syb-chip-a)",
};
const B_ROLE = {
  rgb: "168,85,247", light_rgb: "230,200,255", light2: "var(--lit-purple-15)",
  stone_grad: "radial-gradient(38% 32% at 34% 26%, rgb(240,220,255) 0%, rgb(176,107,255) 22%, rgb(64,22,127) 58%, rgb(10,5,24) 100%)",
  small_grad: "radial-gradient(36% 32% at 32% 26%, rgb(240,220,255), rgb(176,107,255) 26%, rgb(58,20,112) 70%, rgb(8,4,15) 100%)",
  chip: "var(--syb-chip-b)",
};

const ROOT_BG =
  "radial-gradient(60% 40% at 16% 8%, rgba(var(--role-blue-rgb),0.16), transparent 70%)," +
  "radial-gradient(55% 45% at 50% 4%, rgba(var(--role-green-rgb),0.14), transparent 70%)," +
  "radial-gradient(60% 40% at 84% 8%, rgba(var(--role-purple-rgb),0.16), transparent 70%)," +
  "radial-gradient(90% 60% at 50% 110%, rgba(var(--role-green-rgb),0.10), transparent 70%)";

// -------------------------------------------------------------------------
// Entry: decide between RoomList vs Room
// -------------------------------------------------------------------------
export default function Symbiosis() {
  const { share_id } = useParams();
  if (share_id) return <Room shareId={share_id} />;
  return <RoomList />;
}

// =========================================================================
// ROOM LIST — landing when no share_id
// =========================================================================
function RoomList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const { isPhone: P } = useResponsive();

  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(null), 2500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, invs] = await Promise.all([api.listRooms(), api.myInvitations()]);
      setRooms(rs || []);
      setInvitations(invs || []);
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openField = async () => {
    setCreating(true);
    try {
      const r = await api.createRoom("Field of the Third");
      if (inviteEmail.trim()) {
        try {
          await api.inviteRoomByEmail(r.share_id, inviteEmail.trim());
        } catch (e) {
          flash(e?.message || "Could not send invitation");
        }
      }
      navigate(`/symbiosis/room/${r.share_id}`);
    } catch (e) {
      const detail = e?.detail;
      flash(typeof detail === "string" ? detail : (e?.message || "Could not open field"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="syb-room-list" style={{
      flex: "1 1 0", minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch",
      background: "var(--yuo-bg)", color: "var(--yuo-ink)",
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      position: "relative",
    }}>
      <style>{KEYFRAMES}</style>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", background: ROOT_BG }} />

      <div style={{ position: "relative", maxWidth: 780, margin: "0 auto", padding: P ? "10px 16px 14px" : "40px 24px 80px" }}>
        <div style={{ display: "flex", alignItems: P ? "flex-start" : "center", justifyContent: "space-between", marginBottom: P ? 14 : 34 }}>
          <div>
            <div style={{ color: "var(--fg-5)", fontSize: P ? 9 : 10, letterSpacing: P ? "0.3em" : "0.34em", textTransform: "uppercase", marginBottom: 8 }}>
              Symbiosis
            </div>
            <div style={{ fontSize: P ? 26 : 28, lineHeight: P ? 1.2 : undefined, letterSpacing: -0.4, color: "var(--yuo-ink)" }}>
              Field of the Third
            </div>
          </div>
          {P ? (
            <button data-testid="syb-back" onClick={() => navigate("/mirror")}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, flex: "none",
                padding: "10px 12px", borderRadius: 999, border: "none", cursor: "pointer",
                background: "var(--fg-05)", color: "var(--fg-6)", fontFamily: "inherit",
                fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase",
              }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "radial-gradient(38% 34% at 32% 26%, #fff 0%, #5b8fff 22%, #13224a 62%, #05060c 100%)" }} />
              mirror
            </button>
          ) : (
          <button
            data-testid="syb-back"
            onClick={() => navigate("/mirror")}
            style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              background: "transparent", border: "none", padding: 0, cursor: "pointer",
              color: "var(--fg-55)", fontFamily: '"JetBrains Mono", ui-monospace, monospace',
              fontSize: 10.5, letterSpacing: "0.28em", textTransform: "uppercase",
            }}
          >
            <span style={{
              width: 14, height: 14, borderRadius: "50%",
              background: "radial-gradient(34% 30% at 32% 26%, #fff, #4F9DFF 22%, #05060c 100%)",
              boxShadow: "0 0 12px rgba(79,157,255,0.8)",
            }} />
            <span>mirror</span>
          </button>
          )}
        </div>

        {/* Open field */}
        <div style={{
          padding: P ? 18 : 24, borderRadius: P ? 28 : 26, marginBottom: P ? 14 : 22,
          background: "radial-gradient(120% 100% at 20% 0%, rgba(var(--role-green-rgb),0.14), rgba(var(--role-green-rgb),0.04) 70%, transparent 100%)",
          boxShadow: "0 0 60px rgba(var(--role-green-rgb),0.22) inset",
        }}>
          <div style={{ color: "var(--lit-green-1)", fontSize: P ? 9 : 10, letterSpacing: P ? "0.24em" : "0.34em", textTransform: "uppercase", marginBottom: 12 }}>
            Open the field · handshake
          </div>
          <div style={P ? { display: "flex", flexDirection: "column", gap: 12 } : { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input
              data-testid="syb-invite-email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Google email of the second person"
              style={P ? {
                width: "100%", height: 48, boxSizing: "border-box",
                background: "var(--fg-05)", border: "none", outline: "none",
                borderRadius: 999, padding: "0 18px", color: "var(--yuo-ink)",
                boxShadow: "inset 0 0 0 1px rgba(var(--role-blue-rgb),0.3)",
                fontFamily: "inherit", fontSize: 12,
              } : {
                flex: "1 1 260px", minWidth: 220,
                background: "var(--fg-05)",
                border: "none", outline: "none",
                borderRadius: 999, padding: "14px 20px",
                color: "var(--yuo-ink)",
                fontFamily: 'inherit', fontSize: 13.5,
              }}
            />
            <button
              data-testid="syb-open-field"
              onClick={openField}
              disabled={creating}
              style={{
                borderRadius: 999, padding: P ? "0 18px" : "14px 26px", height: P ? 48 : undefined, width: P ? "100%" : undefined,
                fontSize: P ? 10.5 : 11, letterSpacing: "0.22em", textTransform: "uppercase",
                background: "radial-gradient(120% 160% at 20% 0%, rgba(var(--role-green-rgb),0.34), rgba(var(--role-green-rgb),0.08))",
                boxShadow: P ? "0 0 16px rgba(var(--role-green-rgb),0.3)" : "0 0 34px rgba(var(--role-green-rgb),0.32)",
                color: "var(--yuo-ink)", border: "none",
                cursor: "pointer", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", justifyContent: P ? "center" : undefined, gap: P ? 10 : 12,
              }}
            >
              <span style={{
                width: P ? 14 : 16, height: P ? 14 : 16, borderRadius: "50%",
                background: "radial-gradient(38% 32% at 34% 26%, rgb(255,255,255) 0%, rgb(77,255,160) 20%, rgb(13,58,38) 60%, rgb(1,4,10) 100%)",
                boxShadow: "0 0 12px rgba(var(--role-green-rgb),0.55)",
              }} />
              {creating ? "opening…" : "open field"}
            </button>
          </div>
          <div style={{ color: P ? "var(--fg-5)" : "var(--fg-4)", fontSize: P ? 10.5 : 11, marginTop: P ? 12 : 14, lineHeight: P ? 1.6 : 1.55 }}>
            {P ? "If they are already in YUO, an invitation resonates in their room. If not, the seat is held for them."
               : "Enter the Google email of the second person. If they are already in YUO, an invitation resonates in their room. If not, the seat is held for them."}
          </div>
        </div>

        {/* Pending invitations */}
        {invitations.length > 0 && (
          <div style={{ marginBottom: 26 }}>
            <div style={{ color: "var(--fg-5)", fontSize: 10, letterSpacing: "0.34em", textTransform: "uppercase", marginBottom: 12 }}>
              Waiting for you
            </div>
            {invitations.map((inv) => (
              <div key={inv.share_id} data-testid={`syb-invitation-${inv.share_id}`}
                onClick={() => navigate(`/symbiosis/room/${inv.share_id}`)}
                style={{
                  padding: 20, borderRadius: 24, marginBottom: 12,
                  background: "radial-gradient(120% 100% at 20% 0%, rgba(var(--role-green-rgb),0.16), rgba(var(--role-green-rgb),0.04) 70%, transparent 100%)",
                  boxShadow: "0 0 40px rgba(var(--role-green-rgb),0.20) inset",
                  cursor: "pointer",
                }}>
                <div style={{ color: "var(--lit-green-1)", fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 6 }}>
                  invited by {inv.invited_by || inv.owner_name}
                </div>
                <div style={{ fontSize: 15 }}>{inv.title}</div>
              </div>
            ))}
          </div>
        )}

        {/* My rooms */}
        <div style={{ color: "var(--fg-5)", fontSize: P ? 9 : 10, letterSpacing: P ? "0.3em" : "0.34em", textTransform: "uppercase", marginBottom: 12 }}>
          Your fields
        </div>
        {loading ? (
          <div style={{ color: "var(--fg-5)", fontSize: 13 }}>opening…</div>
        ) : rooms.length === 0 ? (
          <div data-testid="syb-empty" style={{ color: "var(--fg-4)", fontSize: 13, padding: 20 }}>
            No fields yet. Open one above.
          </div>
        ) : (
          rooms.map((r) => (
            <RoomRow key={r.share_id} r={r} meId={user?.user_id} phone={P}
              onEnter={() => navigate(`/symbiosis/room/${r.share_id}`)}
              onChanged={load} flash={flash} />
          ))
        )}

        {notice && (
          <div data-testid="syb-notice" style={{
            position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)",
            padding: "10px 18px", borderRadius: 999,
            background: "rgba(var(--role-coral-rgb),0.14)", color: "var(--lit-coral-14)", fontSize: 12,
          }}>{notice}</div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Room row (list) — stone opens the 4-item menu: Enter / Save PDF / Rename / Delete
// -------------------------------------------------------------------------
const MENU_ICONS = {
  enter:  <path d="M5 12h14M13 6l6 6-6 6" />,
  pdf:    <path d="M12 4v11M7 11l5 5 5-5M5 20h14" />,
  rename: <path d="M4 20h4l11-11-4-4L4 16v4zM13 7l4 4" />,
  delete: <path d="M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13M10 11v6M14 11v6" />,
};
const MENU_ITEMS = [
  { key: "enter",  label: "Enter",    color: "var(--lit-blue-18)" },
  { key: "pdf",    label: "Save PDF", color: "var(--lit-green-7)"  },
  { key: "rename", label: "Rename",   color: "var(--lit-purple-15)" },
  { key: "delete", label: "Delete",   color: "var(--lit-coral-14)"          },
];

function RoomMenu({ onPick, onClose, shareId }) {
  useEffect(() => {
    const h = () => onClose();
    window.addEventListener("pointerdown", h);
    return () => window.removeEventListener("pointerdown", h);
  }, [onClose]);
  return (
    <div data-testid={`syb-room-menu-${shareId}`} onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 20, minWidth: 220,
        borderRadius: 22, padding: "6px 0", background: "var(--dk-101220-9)",
        backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
        boxShadow: "0 0 0 1px var(--fg-06), 0 18px 50px rgba(0,0,0,0.55)",
        animation: "syb-emerge .35s cubic-bezier(.2,.7,.2,1) both",
      }}>
      {MENU_ITEMS.map((it, i) => (
        <button key={it.key} type="button" data-testid={`syb-room-menu-${it.key}-${shareId}`}
          aria-label={it.label}
          onClick={(e) => { e.stopPropagation(); onPick(it.key); }}
          style={{
            display: "flex", alignItems: "center", gap: 14, width: "100%",
            padding: "15px 24px", background: "transparent", border: "none", cursor: "pointer",
            color: "var(--yuo-ink)", fontFamily: "inherit", fontSize: 12, letterSpacing: "0.22em",
            textTransform: "uppercase", textAlign: "left", borderRadius: 0, boxShadow: "none",
            borderTop: i ? "1px solid var(--fg-06)" : "none",
          }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={it.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            {MENU_ICONS[it.key]}
          </svg>
          {it.label}
        </button>
      ))}
    </div>
  );
}

function RoomRow({ r, meId, onEnter, onChanged, flash, phone = false }) {
  const [menu, setMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(r.title || "");
  const [busy, setBusy] = useState(false);
  const roleRgb = r.role === "owner" ? A_ROLE.rgb : B_ROLE.rgb;
  const pd = r.pending_delete;
  const askedByMe = pd && pd.by_user_id === meId;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) flash(okMsg); await onChanged(); }
    catch (e) { flash(e?.detail || e?.message || "Something went wrong"); }
    finally { setBusy(false); }
  };

  const pick = async (key) => {
    setMenu(false);
    if (key === "enter") return onEnter();
    if (key === "rename") { setDraft(r.title || ""); setRenaming(true); return; }
    if (key === "pdf") {
      return act(async () => {
        const data = await api.getRoom(r.share_id);
        const { saveRoomPdf } = await import("@/lib/roomPdf");
        await saveRoomPdf({ ...r, ...data.room, title: r.title }, data.messages || []);
      });
    }
    if (key === "delete") {
      return act(() => api.deleteRoomAction(r.share_id, "request"));
    }
  };

  const commitRename = () => {
    setRenaming(false);
    const t = draft.trim();
    if (t === (r.title || "")) return;
    act(() => api.renameRoom(r.share_id, t));
  };

  return (
    <div data-testid={`syb-room-${r.share_id}`}
      onClick={() => { if (!renaming && !menu) onEnter(); }}
      style={{
        position: "relative", padding: phone ? "16px 18px" : 18, borderRadius: phone ? 24 : 22, marginBottom: 10,
        background: phone ? "var(--m-row-bg)" : "var(--fg-04)", cursor: "pointer", opacity: busy ? 0.6 : 1,
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: phone ? 12 : 14 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {renaming ? (
            <input autoFocus data-testid={`syb-rename-input-${r.share_id}`}
              value={draft} onChange={(e) => setDraft(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={commitRename}
              onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenaming(false); }}
              style={{
                width: "100%", background: "var(--fg-06)", border: "none", outline: "none",
                borderRadius: 999, padding: "8px 16px", color: "var(--yuo-ink)",
                fontFamily: "inherit", fontSize: phone ? 14 : 15,
              }} />
          ) : (
            <div style={{ fontSize: phone ? 14 : 15, lineHeight: phone ? 1.4 : undefined, color: "var(--yuo-ink)", display: "flex", alignItems: "center", gap: 10 }}>
              <span data-testid={`syb-room-title-${r.share_id}`}>{r.title}</span>
              {r.renamed && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--fg-45)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  {MENU_ICONS.rename}
                </svg>
              )}
            </div>
          )}
          <div style={{ color: "var(--fg-42)", fontSize: phone ? 10 : 11, marginTop: phone ? 6 : 4, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span>with {r.other_name || "no one yet"} · {r.role}{r.renamed ? " · renamed for you" : ""}</span>
            {pd && askedByMe && (
              <span data-testid={`syb-delete-pending-${r.share_id}`} style={{
                padding: "4px 12px", borderRadius: 999, fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase",
                color: "var(--lit-coral-14)", boxShadow: "inset 0 0 0 1px rgba(var(--role-coral-rgb),0.45)",
              }}>delete requested · awaiting reply</span>
            )}
            {pd && !askedByMe && (
              <span data-testid={`syb-delete-ask-${r.share_id}`} onClick={(e) => e.stopPropagation()}
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  padding: "4px 12px", borderRadius: 999, fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase",
                  color: "var(--lit-coral-14)", boxShadow: "inset 0 0 0 1px rgba(var(--role-coral-rgb),0.45)",
                }}>{(pd.by_name || "").split(" ")[0]} asks to delete</span>
                <button type="button" data-testid={`syb-delete-accept-${r.share_id}`} aria-label="Accept deletion"
                  onClick={() => act(() => api.deleteRoomAction(r.share_id, "confirm"), "Field closed for both")}
                  style={{ ...pillStyle({ active: true, size: "sm", color: FN.question }), padding: "5px 12px", fontSize: 9.5 }}>
                  accept
                </button>
                <button type="button" data-testid={`syb-delete-decline-${r.share_id}`} aria-label="Decline deletion"
                  onClick={() => act(() => api.deleteRoomAction(r.share_id, "cancel"), "Field stays")}
                  style={{ ...pillStyle({ active: false, size: "sm" }), padding: "5px 12px", fontSize: 9.5 }}>
                  decline
                </button>
              </span>
            )}
          </div>
        </div>
        <div style={{ position: "relative", flex: "0 0 auto" }}>
          <button type="button" data-testid={`syb-room-stone-${r.share_id}`} aria-label="Field menu"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setMenu((v) => !v); }}
            style={{
              width: phone ? 30 : 22, height: phone ? 30 : 22, borderRadius: "50%", padding: 0, border: "none", cursor: "pointer",
              background: r.role === "owner" ? A_ROLE.small_grad : B_ROLE.small_grad,
              boxShadow: phone ? `0 0 14px rgba(${roleRgb},${menu ? 0.7 : 0.4})` : `0 0 12px rgba(${roleRgb},${menu ? 0.7 : 0.35})`,
              display: "block",
            }} />
          {menu && <RoomMenu shareId={r.share_id} onPick={pick} onClose={() => setMenu(false)} />}
        </div>
      </div>
    </div>
  );
}

function pillStyle({ active, size = "md", color = { rgb: "238,240,246" } }) {
  return {
    borderRadius: 999,
    padding: size === "sm" ? "7px 18px" : "8px 22px",
    fontSize: size === "sm" ? 10 : 10.5,
    letterSpacing: "0.2em", textTransform: "uppercase",
    fontFamily: 'inherit', fontWeight: 500,
    color: "var(--yuo-ink)",
    background: active
      ? `radial-gradient(120% 160% at 30% 0%, rgba(${color.rgb},0.34), rgba(${color.rgb},0.10))`
      : `linear-gradient(rgba(${color.rgb},var(--syb-idle-a)), rgba(${color.rgb},var(--syb-idle-a))), var(--fg-05)`,
    boxShadow: active ? `0 0 26px rgba(${color.rgb},0.35)` : `inset 0 0 0 1px rgba(${color.rgb},var(--syb-idle-ring))`,
    border: "none", cursor: "pointer", transition: "filter 200ms",
  };
}

// =========================================================================
// ROOM — the three-column negotiation field
// =========================================================================
function Room({ shareId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPhone } = useResponsive();
  const wsRef = useRef(null);

  const [room, setRoom] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connError, setConnError] = useState(null);
  const [invitePending, setInvitePending] = useState(null);

  const [who, setWho] = useState("a"); // "a" | "b"
  const [mode, setMode] = useState("question"); // question | answer | advise
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null); // message object or null
  const [emailInvite, setEmailInvite] = useState("");
  const [emailInviteOpen, setEmailInviteOpen] = useState(false);

  const meRoleRef = useRef("owner");

  // ---- initial fetch + WS -----------------------------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.getRoom(shareId);
        if (!alive) return;
        setRoom(data.room);
        setMsgs(data.messages || []);
        setInvitePending(data.room?.pending_yuo || null);
        meRoleRef.current = data.room?.role || "guest";
        setWho(data.room?.role === "owner" ? "a" : "b");
      } catch (e) {
        setConnError(e?.detail || e?.message || "Could not enter the field");
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [shareId]);

  useEffect(() => {
    if (loading || connError) return;
    const url = api.roomWebSocketUrl(shareId, user?.name || "Guest");
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      let data;
      try { data = JSON.parse(ev.data); } catch { return; }
      if (data.type === "history") {
        if (data.messages) setMsgs(data.messages);
        return;
      }
      if (data.type === "message") {
        setMsgs((m) => [...m, data.message]);
        return;
      }
      if (data.type === "yuo_invite") {
        setInvitePending(data.invite);
        return;
      }
      if (data.type === "yuo_accept" || data.type === "yuo_cancel") {
        setInvitePending(null);
        return;
      }
    };
    ws.onerror = () => setConnError("Connection lost");
    return () => { try { ws.close(); } catch {} };
  }, [loading, connError, shareId, user?.name]);

  // ---- identity mapping: which side (a|b) does a given user_id map to ----
  const otherName = room?.other_name;
  const ownerName = room?.owner_name;
  const aName = ownerName || "Participant A";
  const bName = otherName || "Waiting…";
  const aColor = room?.participant_totems?.[room?.owner_id]?.color;
  const firstGuest = (room?.participants || [])[0];
  const bColor = firstGuest ? room?.participant_totems?.[firstGuest]?.color : null;

  // Bucket messages by side: owner → left, first guest → right, everyone else → right
  const authorSideOf = (m) => {
    if (m.author_user_id && m.author_user_id === room?.owner_id) return "a";
    if (m.author === ownerName) return "a";
    return "b";
  };
  const sideOf = (m) => {
    if (m.role === "third") return "yuo";
    if (m.to_user_id || m.to_name) {
      if ((m.to_user_id && m.to_user_id === room?.owner_id) || m.to_name === ownerName) return "a";
      return "b";
    }
    return authorSideOf(m);
  };
  const leftMsgs  = useMemo(() => msgs.filter((m) => sideOf(m) === "a"),   [msgs, room]);
  const yuoMsgs   = useMemo(() => msgs.filter((m) => sideOf(m) === "yuo"), [msgs, room]);
  const rightMsgs = useMemo(() => msgs.filter((m) => sideOf(m) === "b"),   [msgs, room]);

  // ---- actions -----------------------------------------------------------
  const send = () => {
    const content = draft.trim();
    if (!content || !wsRef.current || wsRef.current.readyState !== 1) return;
    const mySide = meRoleRef.current === "owner" ? "a" : "b";
    const addressed = who !== mySide;
    wsRef.current.send(JSON.stringify({
      action: "message",
      content,
      mtype: mode,
      user_id: user?.user_id,
      quote: replyTo?.content || null,
      quote_from: replyTo?.author || null,
      quote_from_user_id: replyTo?.author_user_id || null,
      reply_to_id: replyTo?.message_id || null,
      to_name: addressed ? (who === "a" ? aName : bName) : null,
      to_user_id: addressed ? (who === "a" ? room?.owner_id : firstGuest) || null : null,
    }));
    setDraft("");
    setReplyTo(null);
  };

  const askYuo = () => {
    const content = draft.trim() || "What is the Third here?";
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({
      action: "yuo_invite", content, user_id: user?.user_id,
    }));
    setDraft("");
  };

  const acceptYuo = () => {
    wsRef.current?.send(JSON.stringify({ action: "yuo_accept" }));
  };
  const declineYuo = () => {
    wsRef.current?.send(JSON.stringify({ action: "yuo_cancel" }));
  };

  const inviteByEmail = async () => {
    if (!emailInvite.trim()) return;
    try {
      await api.inviteRoomByEmail(shareId, emailInvite.trim());
      setEmailInvite("");
      setEmailInviteOpen(false);
    } catch (e) {
      alert(e?.message || "Could not invite");
    }
  };

  if (connError) {
    return <RoomError message={typeof connError === "string" ? connError : "Could not enter"} onBack={() => navigate("/symbiosis")} />;
  }
  if (loading || !room) return <RoomLoading />;

  const inviteFromSameUser = invitePending?.from_user_id === user?.user_id;
  const canAcceptInvite = invitePending && !inviteFromSameUser;

  if (isPhone) {
    return (
      <PhoneRoom
        room={room} aName={aName} bName={bName} aColor={aColor} bColor={bColor}
        leftMsgs={leftMsgs} yuoMsgs={yuoMsgs} rightMsgs={rightMsgs}
        senderRoleOf={(m) => (authorSideOf(m) === "a" ? A_ROLE : B_ROLE)}
        mySide={meRoleRef.current === "owner" ? "a" : "b"} setWho={setWho}
        mode={mode} setMode={setMode} draft={draft} setDraft={setDraft}
        replyTo={replyTo} setReplyTo={setReplyTo}
        onSend={send} onAskYuo={askYuo} canAskYuo={!invitePending}
        invitation={invitePending && (
          <InvitationCard invite={invitePending} canAccept={canAcceptInvite} onAccept={acceptYuo} onDecline={declineYuo}
            initiatorFirstName={(invitePending.from_name || "").split(" ")[0]} />
        )}
        canInvite={room.role === "owner" && !otherName}
        emailInvite={emailInvite} setEmailInvite={setEmailInvite}
        emailInviteOpen={emailInviteOpen} setEmailInviteOpen={setEmailInviteOpen} inviteByEmail={inviteByEmail}
        onClose={() => navigate("/symbiosis")}
      />
    );
  }

  return (
    <div style={{
      height: "100vh", display: "flex", flexDirection: "column",
      background: "var(--yuo-bg)", color: "var(--yuo-ink)",
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      overflow: "hidden", position: "relative",
    }}>
      <style>{KEYFRAMES}</style>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: ROOT_BG }} />

      {/* Header - minimal, only title + close */}
      <div style={{ position: "relative", flex: "0 0 auto", display: "flex",
        justifyContent: "space-between", alignItems: "center",
        padding: "14px 24px 4px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 10, height: 10, borderRadius: "50%",
            background: "rgba(var(--role-green-rgb),0.9)",
            animation: "syb-breathe 4.5s ease-in-out infinite",
            boxShadow: "0 0 10px rgba(var(--role-green-rgb),0.55)",
          }} />
          <div style={{ fontSize: 11, letterSpacing: "0.36em", textTransform: "uppercase", color: "var(--fg-6)" }}>
            {room.title}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {room.role === "owner" && !otherName && (
            <>
              {!emailInviteOpen ? (
                <button data-testid="syb-invite-open"
                  onClick={() => setEmailInviteOpen(true)}
                  style={pillStyle({ active: false, size: "sm", color: FN.answer })}>
                  + invite
                </button>
              ) : (
                <>
                  <input
                    data-testid="syb-invite-email-input"
                    value={emailInvite}
                    onChange={(e) => setEmailInvite(e.target.value)}
                    placeholder="Google email"
                    onKeyDown={(e) => { if (e.key === "Enter") inviteByEmail(); }}
                    style={{
                      background: "var(--fg-06)", border: "none", outline: "none",
                      borderRadius: 999, padding: "8px 16px",
                      color: "var(--yuo-ink)", fontFamily: 'inherit', fontSize: 12,
                    }}
                  />
                  <button data-testid="syb-invite-send" onClick={inviteByEmail}
                    style={pillStyle({ active: true, size: "sm", color: FN.answer })}>
                    send
                  </button>
                </>
              )}
            </>
          )}
          <button data-testid="syb-close-room" onClick={() => navigate("/symbiosis")}
            style={pillStyle({ active: false, size: "sm" })}>
            close
          </button>
        </div>
      </div>

      {/* Three columns */}
      <div style={{
        flex: "1 1 0", position: "relative", display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr", minHeight: 0,
      }}>
        <Column
          role={A_ROLE}
          name={aName}
          driftDur={26}
          driftReverse={false}
          totemColor={aColor}
          isYuo={false}
          messages={leftMsgs}
          myFirstName={user?.name?.split(" ")[0] || ""}
          onMessageTap={(m) => setReplyTo(m)}
          replyToId={replyTo?.message_id}
          senderRoleOf={(m) => (authorSideOf(m) === "a" ? A_ROLE : B_ROLE)}
        />
        <Column
          role={{ ...FN.answer, chip: "var(--lit-green-1)" }}
          name="YUO"
          driftDur={31}
          driftReverse={true}
          isYuo={true}
          messages={yuoMsgs}
          onMessageTap={(m) => setReplyTo(m)}
          replyToId={replyTo?.message_id}
          extra={
            invitePending && (
              <InvitationCard
                invite={invitePending}
                canAccept={canAcceptInvite}
                onAccept={acceptYuo}
                onDecline={declineYuo}
                initiatorFirstName={(invitePending.from_name || "").split(" ")[0]}
              />
            )
          }
        />
        <Column
          role={B_ROLE}
          name={bName}
          driftDur={29}
          driftReverse={false}
          totemColor={bColor}
          isYuo={false}
          messages={rightMsgs}
          myFirstName={user?.name?.split(" ")[0] || ""}
          onMessageTap={(m) => setReplyTo(m)}
          replyToId={replyTo?.message_id}
          senderRoleOf={(m) => (authorSideOf(m) === "a" ? A_ROLE : B_ROLE)}
        />
      </div>

      {/* Footer */}
      <Footer
        who={who} setWho={setWho}
        aName={aName} bName={bName}
        mode={mode} setMode={setMode}
        draft={draft} setDraft={setDraft}
        replyTo={replyTo} setReplyTo={setReplyTo}
        onSend={send}
        onAskYuo={askYuo}
        canAskYuo={!invitePending}
      />
    </div>
  );
}

// -------------------------------------------------------------------------
// Column
// -------------------------------------------------------------------------
function Column({ role, name, driftDur, driftReverse, isYuo, totemColor, messages, myFirstName, onMessageTap, replyToId, extra, senderRoleOf }) {
  const size = isYuo ? 70 : 64;
  const rgb = isYuo ? "52,255,150" : role.rgb;
  const lightRgb = isYuo ? "160,255,205" : role.light_rgb;
  const outerShadow = isYuo ? "0 0 60px" : "0 0 46px";
  const outerAlpha = isYuo ? 0.55 : 0.5;
  const innerDark = isYuo ? "-9px -11px 24px" : "-8px -10px 22px";
  const innerLight = isYuo ? "7px 7px 16px" : "6px 6px 14px";
  const chipColor = isYuo ? "var(--lit-green-1)" : role.chip;
  const fogRgb = rgb;

  const stoneGrad = isYuo
    ? "radial-gradient(34% 30% at 32% 24%, rgb(255,255,255) 0%, rgb(77,255,160) 14%, rgb(13,58,38) 52%, rgb(1,4,10) 100%)"
    : role.stone_grad;

  return (
    <section style={{ position: "relative", display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      {/* Column fog */}
      <div style={{
        position: "absolute", inset: "-10% -20%", pointerEvents: "none",
        background: `radial-gradient(50% 40% at 50% 30%, rgba(${fogRgb},0.18), transparent 72%)`,
        animation: `syb-drift ${driftDur}s ease-in-out infinite ${driftReverse ? "reverse" : ""}`,
      }} />

      {/* Header: stone + name */}
      <div style={{ position: "relative", flex: "0 0 auto", display: "flex",
        flexDirection: "column", alignItems: "center", gap: 10, padding: "14px 0 18px" }}>
        <div style={{
          position: "relative", width: size, height: size, borderRadius: "50%",
          background: stoneGrad,
          boxShadow: `${outerShadow} rgba(${rgb},${outerAlpha}), ${innerDark} rgba(0,0,0,0.75) inset, ${innerLight} rgba(${lightRgb},0.35) inset`,
        }}>
          {isYuo ? (
            <>
              {/* Facets (conic wedges) */}
              <div style={{
                position: "absolute", inset: 2, borderRadius: "50%",
                background:
                  "conic-gradient(transparent 0deg, rgba(255,255,255,0.5) 12deg, transparent 26deg," +
                  " transparent 120deg, rgba(180,255,220,0.4) 132deg, transparent 148deg," +
                  " transparent 250deg, rgba(255,255,255,0.35) 262deg, transparent 276deg)",
                mixBlendMode: "screen",
                animation: "syb-spin 22s linear infinite",
              }} />
              {/* Dust */}
              <div style={{
                position: "absolute", inset: 0, borderRadius: "50%",
                background:
                  "radial-gradient(1.5px 1.5px at 24% 62%, rgba(255,255,255,0.95), transparent)," +
                  "radial-gradient(1.5px 1.5px at 68% 30%, rgba(255,255,255,0.9), transparent)," +
                  "radial-gradient(1.5px 1.5px at 52% 76%, rgba(200,255,225,0.9), transparent)," +
                  "radial-gradient(1px 1px at 78% 58%, rgba(255,255,255,0.8), transparent)," +
                  "radial-gradient(1px 1px at 38% 40%, rgba(255,255,255,0.7), transparent)",
                animation: "syb-breathe 6s ease-in-out infinite",
              }} />
            </>
          ) : (
            <div style={{
              position: "absolute", inset: 0, borderRadius: "50%",
              background: `radial-gradient(22% 18% at 70% 76%, rgba(${lightRgb},0.55), transparent 70%)`,
            }} />
          )}
        </div>
        <div style={{ fontSize: 13, letterSpacing: isYuo ? "0.3em" : "0.26em", textTransform: "uppercase", color: chipColor }}>
          {name}
        </div>
        {totemColor && (
          <div title="Personalization totem" style={{
            width: 8, height: 8, borderRadius: "50%",
            background: totemColor, boxShadow: `0 0 10px ${totemColor}`,
          }} />
        )}
      </div>

      {/* Messages */}
      <div style={{
        position: "relative", flex: "1 1 0", overflow: "auto",
        padding: "8px 26px 26px", display: "flex", flexDirection: "column", gap: 26,
      }}>
        {messages.map((m) => (
          <MessageBubble key={m.message_id} m={m} isYuo={isYuo} role={role}
            fromRole={(m.to_name || m.to_user_id) && senderRoleOf ? senderRoleOf(m) : null}
            selected={m.message_id === replyToId}
            onTap={() => onMessageTap?.(m)} />
        ))}
        {extra}
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------
// Message bubble
// -------------------------------------------------------------------------
function MessageBubble({ m, isYuo, role, selected, onTap, fromRole }) {
  const type = m.type || (isYuo ? "answer" : "question");
  const fn = FN[type] || FN.question;
  const radius = isYuo ? 28 : 26;
  const bg = `radial-gradient(120% 100% at 24% 0%, rgba(${fn.rgb},0.20), rgba(${fn.rgb},0.05) 62%, transparent 100%)`;

  const time = m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

  const [translation, setTranslation] = useState(() => trGet(m.message_id, uiLang()));
  const [shown, setShown] = useState(() => !!translation);
  const [busy, setBusy] = useState(false);

  const toggleTranslate = async (e) => {
    e.stopPropagation();
    if (busy) return;
    if (shown) { setShown(false); return; }
    const lang = uiLang();
    const cached = trGet(m.message_id, lang);
    if (cached) { setTranslation(cached); setShown(true); return; }
    setBusy(true);
    try {
      const r = await api.translate(m.content, lang);
      trSet(m.message_id, lang, r.translation);
      setTranslation(r.translation);
      setShown(true);
    } catch { /* silent — original stays visible */ }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`syb-msg-${m.message_id}`}
      className="syb-msg"
      onClick={onTap}
      style={{
        position: "relative",
        cursor: "pointer",
        display: "flex", flexDirection: "column", gap: 9,
        padding: "20px 22px",
        borderRadius: radius,
        background: bg,
        animation: `syb-emerge ${isYuo ? "1.1s" : ".9s"} cubic-bezier(.2,.7,.2,1) both`,
        filter: selected ? "brightness(1.22)" : "none",
        boxShadow: fromRole ? `inset 0 0 0 1px rgba(${fromRole.rgb},0.6), 0 0 18px rgba(${fromRole.rgb},0.18)` : "none",
      }}>
      {fromRole && (
        <div data-testid={`syb-from-${m.message_id}`} style={{
          fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase",
          color: fromRole.light2, display: "flex", gap: 8, alignItems: "center",
        }}>
          <span aria-hidden="true">→</span>
          <span>{(m.author || "").split(" ")[0]}</span>
        </div>
      )}
      <button type="button" data-testid={`syb-translate-${m.message_id}`}
        aria-label="Translate" title="Translate"
        onClick={toggleTranslate}
        className={`syb-tr-btn${shown ? " on" : ""}${busy ? " busy" : ""}`}
        style={{
          position: "absolute", top: 10, right: 10, width: 22, height: 22, padding: 0,
          display: "grid", placeItems: "center", borderRadius: "50%",
          background: "transparent", border: "none", boxShadow: "none", cursor: "pointer",
          color: shown ? "var(--lit-green-7)" : "var(--fg-7)",
          filter: shown ? "drop-shadow(0 0 6px rgba(52,255,155,0.7))" : "none",
          WebkitTapHighlightColor: "transparent",
        }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9.5" />
          <path d="M2.5 12h19M12 2.5c2.9 3 4.3 6.2 4.3 9.5s-1.4 6.5-4.3 9.5M12 2.5C9.1 5.5 7.7 8.7 7.7 12s1.4 6.5 4.3 9.5" />
        </svg>
      </button>
      {m.quote && (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", opacity: 0.62 }}>
          <div style={{
            flex: "0 0 auto", width: 16, height: 16, borderRadius: "50%",
            background: role?.small_grad ||
              "radial-gradient(36% 32% at 32% 26%, rgb(207,230,255), rgb(79,157,255) 26%, rgb(13,47,92) 70%, rgb(4,7,15) 100%)",
            marginTop: 2,
          }} />
          <div style={{ fontSize: 10.5, lineHeight: 1.4, color: "var(--fg-6)" }}>
            {m.quote}
          </div>
        </div>
      )}
      {shown && translation ? (
        <div data-testid={`syb-translated-${m.message_id}`} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 9.5, letterSpacing: "0.22em", textTransform: "lowercase", color: "var(--lit-green-7)" }}>
            translated
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--yuo-ink)", whiteSpace: "pre-wrap" }}>
            {translation}
          </div>
          <div style={{ height: 1, background: "var(--fg-14)" }} />
          <div style={{ fontSize: 11, lineHeight: 1.5, fontStyle: "italic", color: "var(--fg-55)", whiteSpace: "pre-wrap" }}>
            {m.content}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--yuo-ink)", whiteSpace: "pre-wrap" }}>
          {m.content}
        </div>
      )}
      {time && (
        <div style={{ fontSize: 9.5, letterSpacing: "0.2em", color: "var(--fg-3)" }}>{time}</div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// YUO invitation card (three's meeting)
// -------------------------------------------------------------------------
function InvitationCard({ invite, canAccept, onAccept, onDecline, initiatorFirstName }) {
  return (
    <div data-testid="syb-yuo-invite" style={{
      padding: "22px 22px 20px", borderRadius: 30,
      background: "radial-gradient(120% 100% at 24% 0%, rgba(52,255,155,0.22), rgba(52,255,155,0.06) 62%, transparent 100%)",
      boxShadow: "0 0 60px rgba(var(--role-green-rgb),0.22) inset",
      animation: "syb-emerge 1s cubic-bezier(.2,.7,.2,1) both",
      display: "flex", flexDirection: "column", gap: 14,
    }}>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 10,
      }}>
        <div style={{
          width: 10, height: 10, borderRadius: "50%", background: "rgb(77,255,160)",
          animation: "syb-breathe 2.4s ease-in-out infinite",
          boxShadow: "0 0 10px rgba(var(--role-green-rgb),0.7)",
        }} />
        <div style={{ fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase", color: "var(--lit-green-1)" }}>
          meeting of the Third
        </div>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--yuo-ink)" }}>
        <span style={{ color: "var(--lit-green-1)" }}>{invite.from_name || "…"}:</span>{" "}
        {invite.prompt || "What is the Third here?"}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button data-testid="syb-yuo-accept"
          onClick={onAccept}
          disabled={!canAccept}
          style={{
            ...pillStyle({ active: true, size: "md", color: FN.answer }),
            opacity: canAccept ? 1 : 0.5,
            cursor: canAccept ? "pointer" : "not-allowed",
          }}>
          let in{initiatorFirstName ? ` · ${initiatorFirstName}` : ""}
        </button>
        <button data-testid="syb-yuo-decline"
          onClick={onDecline}
          style={pillStyle({ active: false, size: "md" })}>
          later
        </button>
      </div>
      {!canAccept && (
        <div style={{ color: "var(--fg-42)", fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase" }}>
          waiting for the other's confirmation
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Footer (author pills, function pills, ASK YUO, input, send stone)
// -------------------------------------------------------------------------
function Footer({ who, setWho, aName, bName, mode, setMode, draft, setDraft, replyTo, setReplyTo, onSend, onAskYuo, canAskYuo }) {
  const modeMeta = FN[mode];

  return (
    <div style={{
      position: "relative", flex: "0 0 auto",
      padding: "14px 22px 22px",
      display: "grid", gap: 12,
    }}>
      {/* Row 1 — author pills + function pills + ASK YUO */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", alignItems: "center" }}>
        <button data-testid="syb-who-a" onClick={() => setWho("a")}
          style={pillStyle({ active: who === "a", size: "sm", color: A_ROLE })}>
          {aName}
        </button>
        <button data-testid="syb-who-b" onClick={() => setWho("b")}
          style={pillStyle({ active: who === "b", size: "sm", color: B_ROLE })}>
          {bName}
        </button>

        <div style={{ width: 16 }} />

        {(["question", "answer", "advise"]).map((k) => (
          <button key={k} data-testid={`syb-mode-${k}`}
            onClick={() => setMode(k)}
            style={pillStyle({ active: mode === k, size: "md", color: FN[k] })}>
            {FN[k].label}
          </button>
        ))}

        <div style={{ width: 12 }} />

        <button data-testid="syb-ask-yuo" onClick={canAskYuo ? onAskYuo : undefined}
          disabled={!canAskYuo}
          style={{
            borderRadius: 999, padding: "15px 26px",
            fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
            background: "radial-gradient(120% 160% at 20% 0%, rgba(var(--role-green-rgb),0.34), rgba(var(--role-green-rgb),0.08))",
            boxShadow: "0 0 34px rgba(var(--role-green-rgb),0.32)",
            color: "var(--yuo-ink)", border: "none",
            cursor: canAskYuo ? "pointer" : "not-allowed",
            opacity: canAskYuo ? 1 : 0.5,
            fontFamily: 'inherit',
            display: "inline-flex", alignItems: "center", gap: 12,
          }}>
          <span style={{
            width: 16, height: 16, borderRadius: "50%",
            background: "radial-gradient(38% 32% at 34% 26%, rgb(255,255,255) 0%, rgb(77,255,160) 20%, rgb(13,58,38) 60%, rgb(1,4,10) 100%)",
            boxShadow: "0 0 12px rgba(var(--role-green-rgb),0.55)",
          }} />
          ASK YUO
        </button>
      </div>

      {/* Row 2 — input capsule with mode dot + label + send stone */}
      <div style={{
        display: "flex", alignItems: "flex-end", gap: 10,
        maxWidth: 820, margin: "0 auto", width: "100%",
      }}>
        <div style={{
          flex: 1, display: "flex", alignItems: "flex-end", gap: 12,
          padding: "6px 8px 6px 28px", borderRadius: 31,
          background: "var(--fg-05)",
          boxShadow: `0 0 44px rgba(${modeMeta.rgb},0.22) inset`,
        }}>
          {/* mode dot */}
          <span style={{
            width: 9, height: 9, borderRadius: "50%", marginBottom: 21,
            background: modeMeta.hex,
            boxShadow: `0 0 14px rgba(${modeMeta.rgb},1)`,
          }} />
          {/* mode label */}
          <span style={{
            fontSize: 9.5, letterSpacing: "0.24em", textTransform: "uppercase", marginBottom: 20,
            color: modeMeta.text, whiteSpace: "nowrap",
          }}>
            {modeMeta.label}
          </span>
          <div style={{
            fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase",
            color: "var(--fg-4)",
            marginLeft: 8, whiteSpace: "nowrap", marginBottom: 19,
          }}>
            {replyTo ? (
              <span style={{ color: "var(--fg-7)" }}>
                in reply to {replyTo.author}
                <button
                  onClick={(e) => { e.stopPropagation(); setReplyTo(null); }}
                  style={{
                    background: "transparent", border: "none",
                    color: "var(--fg-4)", marginLeft: 8,
                    cursor: "pointer", fontFamily: 'inherit', fontSize: 10,
                  }}>×</button>
              </span>
            ) : "new thread"}
          </div>
          <AutoTextarea
            data-testid="syb-input"
            value={draft}
            onChange={setDraft}
            onSend={onSend}
            placeholder=""
            maxHeight={200}
            style={{
              flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none",
              padding: "16px 0", color: "var(--yuo-ink)",
              fontFamily: 'inherit', fontSize: 13.5,
            }}
          />
        </div>

        {/* Send stone (no glyph) */}
        <button
          data-testid="syb-send"
          onClick={onSend}
          disabled={!draft.trim()}
          aria-label="send"
          className="yuo-send-stone"
          style={{
            width: 52, height: 52, borderRadius: "50%", border: "none",
            background: "radial-gradient(38% 34% at 34% 26%, #ffffff, #4dffa0 20%, #0d3a26 60%, #01040a 100%)",
            boxShadow:
              "0 0 30px rgba(var(--role-green-rgb),0.55)," +
              "inset -6px -8px 16px rgba(0,0,0,0.8)",
            cursor: draft.trim() ? "pointer" : "default",
            opacity: draft.trim() ? 1 : 0.5,
            padding: 0,
          }}
        />
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Phone (≤600px) working space — v6 block 09: three swipe pages, Third first
// -------------------------------------------------------------------------
const stoneOf = (c, d) => `radial-gradient(38% 34% at 32% 26%, #fff 0%, ${c} 22%, ${d} 62%, #05060c 100%)`;
const YUO_FACET = "conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.45) 12deg, transparent 28deg, transparent 150deg, rgba(255,255,255,0.3) 162deg, transparent 178deg)";
const PHONE_MODES = [
  { key: "question", label: "QUESTION", color: "#ff7a62", bg: "rgba(60,40,40,0.5)",  stone: stoneOf("#ff6a55", "#3a1410") },
  { key: "answer",   label: "ANSWER",   color: "#c9d3e6", bg: "rgba(40,44,56,0.6)",  stone: stoneOf("#b9c6de", "#2b3242") },
  { key: "advise",   label: "ADVISE",   color: "#e9b44c", bg: "rgba(60,50,24,0.5)",  stone: stoneOf("#e9b44c", "#3a2a10") },
  { key: "askyuo",   label: "ASK YUO",  color: "#5cf0a6", bg: "rgba(24,60,44,0.55)", stone: stoneOf("#3ddc97", "#0d3a28") },
];
const PHONE_GLOWS = [
  "radial-gradient(90% 50% at 20% 10%, rgba(30,70,170,0.40), transparent 70%)",
  "radial-gradient(90% 50% at 50% 10%, rgba(30,140,90,0.40), transparent 70%)",
  "radial-gradient(90% 50% at 80% 10%, rgba(110,50,170,0.40), transparent 70%)",
];
const firstName = (n) => (n || "").split(" ")[0].toUpperCase();

function PhoneRoom(p) {
  const [col, setCol] = useState(1);
  const [pm, setPm] = useState(() => Math.max(0, PHONE_MODES.findIndex((m) => m.key === p.mode)));
  const scrollRef = useRef(null);
  const colRef = useRef(1);

  const syncWho = (i) => { if (i === 0) p.setWho("a"); else if (i === 2) p.setWho("b"); else p.setWho(p.mySide); };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.clientWidth * colRef.current;
    syncWho(colRef.current);
    const onResize = () => { const e = scrollRef.current; if (e) e.scrollLeft = e.clientWidth * colRef.current; };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (i) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ left: el.clientWidth * i, behavior: "smooth" });
    colRef.current = i; setCol(i); syncWho(i);
  };
  const onScroll = (e) => {
    const el = e.currentTarget;
    const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
    if (i !== colRef.current) { colRef.current = i; setCol(i); syncWho(i); }
  };
  const cycleMode = () => {
    const next = (pm + 1) % PHONE_MODES.length;
    setPm(next);
    if (PHONE_MODES[next].key !== "askyuo") p.setMode(PHONE_MODES[next].key);
  };

  const voices = [
    { n: firstName(p.aName) || "A", rgb: "80,140,255", c: "#5b8fff", stone: A_ROLE.stone_grad, totem: p.aColor },
    { n: "YUO · THIRD", rgb: "61,220,151", c: "#3ddc97", stone: stoneOf("#3ddc97", "#0d3a28"), facet: true },
    { n: firstName(p.bName) || "B", rgb: "162,107,255", c: "#a26bff", stone: B_ROLE.stone_grad, totem: p.bColor },
  ];
  const toLabel = [`TO ${firstName(p.aName)}`, "TO THE THIRD", `TO ${firstName(p.bName)}`][col];
  const m = PHONE_MODES[pm];
  const asking = m.key === "askyuo";
  const sendDisabled = asking ? !p.canAskYuo : !p.draft.trim();
  const pages = [
    { msgs: p.leftMsgs, role: A_ROLE, isYuo: false },
    { msgs: p.yuoMsgs, role: { ...FN.answer, chip: "var(--lit-green-1)" }, isYuo: true },
    { msgs: p.rightMsgs, role: B_ROLE, isYuo: false },
  ];

  return (
    <div data-testid="syb-phone-room" style={{
      flex: "1 1 0", minHeight: 0, display: "flex", flexDirection: "column", position: "relative", overflow: "hidden",
      background: "var(--yuo-bg)", color: "var(--yuo-ink)", fontFamily: '"JetBrains Mono", ui-monospace, monospace',
    }}>
      <style>{KEYFRAMES}</style>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: PHONE_GLOWS[col], transition: "background .8s ease" }} />

      {/* Header */}
      <div style={{ position: "relative", flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "4px 16px 8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ flex: "none", width: 8, height: 8, borderRadius: "50%", background: "#3ddc97", boxShadow: "0 0 10px #3ddc97", animation: "syb-breathe 4.5s ease-in-out infinite" }} />
          <span data-testid="syb-phone-title" style={{ fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--fg-7)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Working space · {p.room.title}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
          {p.canInvite && !p.emailInviteOpen && (
            <button data-testid="syb-invite-open" onClick={() => p.setEmailInviteOpen(true)}
              style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", padding: "11px 13px", borderRadius: 999, border: "none", background: "var(--fg-06)", color: "var(--yuo-ink)", fontFamily: "inherit", cursor: "pointer" }}>
              + invite
            </button>
          )}
          <button data-testid="syb-close-room" onClick={p.onClose}
            style={{ fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", padding: "11px 13px", borderRadius: 999, border: "none", background: "var(--fg-06)", color: "var(--yuo-ink)", fontFamily: "inherit", cursor: "pointer" }}>
            close
          </button>
        </div>
      </div>
      {p.canInvite && p.emailInviteOpen && (
        <div style={{ position: "relative", flex: "none", display: "flex", gap: 8, padding: "0 16px 8px" }}>
          <input data-testid="syb-invite-email-input" value={p.emailInvite} onChange={(e) => p.setEmailInvite(e.target.value)} placeholder="Google email"
            onKeyDown={(e) => { if (e.key === "Enter") p.inviteByEmail(); }}
            style={{ flex: 1, minWidth: 0, height: 40, background: "var(--fg-06)", border: "none", outline: "none", borderRadius: 999, padding: "0 16px", color: "var(--yuo-ink)", fontFamily: "inherit", fontSize: 12 }} />
          <button data-testid="syb-invite-send" onClick={p.inviteByEmail} style={pillStyle({ active: true, size: "sm", color: FN.answer })}>send</button>
        </div>
      )}

      {/* Voices */}
      <div data-testid="syb-voices" style={{ position: "relative", flex: "none", display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", alignItems: "end", padding: "4px 10px 10px" }}>
        {voices.map((v, i) => {
          const on = i === col;
          const s = on ? 54 : 34;
          return (
            <button key={v.n + i} type="button" data-testid={`syb-voice-${i}`} aria-label={v.n} onClick={() => go(i)}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "6px 0", border: "none", background: "none", cursor: "pointer", color: "inherit", minHeight: 44, boxShadow: "none" }}>
              <span style={{
                position: "relative", width: s, height: s, borderRadius: "50%", background: v.stone, opacity: on ? 1 : 0.6, transition: "all .5s ease",
                boxShadow: on ? `0 0 34px rgba(${v.rgb},0.55), inset -5px -6px 13px rgba(0,0,0,0.85)` : "inset -4px -5px 10px rgba(0,0,0,0.85)",
              }}>
                {v.facet && <span style={{ position: "absolute", inset: 1, borderRadius: "50%", background: YUO_FACET, mixBlendMode: "screen", animation: "syb-spin 26s linear infinite" }} />}
                {v.totem && <span style={{ position: "absolute", left: "50%", bottom: -6, transform: "translateX(-50%)", width: 6, height: 6, borderRadius: "50%", background: v.totem, boxShadow: `0 0 8px ${v.totem}` }} />}
              </span>
              <span style={{ fontSize: 8.5, letterSpacing: "0.22em", color: on ? "var(--yuo-ink)" : "var(--fg-38)", transition: "color .5s ease", whiteSpace: "nowrap" }}>{v.n}</span>
            </button>
          );
        })}
      </div>
      <div data-testid="syb-page-dots" style={{ position: "relative", flex: "none", display: "flex", justifyContent: "center", gap: 6, paddingBottom: 8 }}>
        {voices.map((v, i) => (
          <span key={i} style={{ width: i === col ? 22 : 6, height: 4, borderRadius: 999, background: i === col ? v.c : "var(--fg-22)", transition: "all .4s ease" }} />
        ))}
      </div>

      {/* Pages */}
      <div ref={scrollRef} onScroll={onScroll} className="m-scroll" data-testid="syb-pages"
        style={{ position: "relative", flex: "1 1 0", minHeight: 0, display: "flex", overflowX: "auto", overflowY: "hidden", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}>
        {pages.map((pg, i) => (
          <div key={i} className="m-scroll" data-testid={`syb-page-${i}`}
            style={{ flex: "none", width: "100%", height: "100%", overflowY: "auto", scrollSnapAlign: "start", scrollSnapStop: "always", boxSizing: "border-box", padding: "4px 14px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
            {pg.msgs.map((msg) => (
              <MessageBubble key={msg.message_id} m={msg} isYuo={pg.isYuo} role={pg.role}
                fromRole={(msg.to_name || msg.to_user_id) && !pg.isYuo ? p.senderRoleOf(msg) : null}
                selected={msg.message_id === p.replyTo?.message_id}
                onTap={() => p.setReplyTo(msg)} />
            ))}
            {i === 1 && p.invitation}
          </div>
        ))}
      </div>

      {/* Input row */}
      <div style={{ position: "relative", flex: "none", display: "flex", flexDirection: "column", gap: 8, padding: "10px 0 10px", background: "var(--m-dock-bg)" }}>
        {p.replyTo && (
          <div data-testid="syb-phone-reply" style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 22px", fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--fg-7)" }}>
            in reply to {p.replyTo.author}
            <button type="button" aria-label="Cancel reply" onClick={() => p.setReplyTo(null)} style={{ background: "transparent", border: "none", color: "var(--fg-4)", cursor: "pointer", fontFamily: "inherit", fontSize: 12, padding: "0 4px" }}>×</button>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, padding: "0 14px" }}>
          <button type="button" data-testid="syb-mode-stone" aria-label={`Message type: ${m.label}`} onClick={cycleMode}
            style={{ flex: "none", width: 42, height: 42, display: "flex", alignItems: "center", justifyContent: "center", border: "none", borderRadius: "50%", background: m.bg, boxShadow: `0 0 18px ${m.color}66`, cursor: "pointer", transition: "all .3s ease" }}>
            <span style={{ width: 20, height: 20, borderRadius: "50%", background: m.stone }} />
          </button>
          <div style={{ flex: 1, minWidth: 0, minHeight: 42, display: "flex", alignItems: "flex-end", gap: 10, padding: "0 16px", borderRadius: 21, background: m.bg, transition: "background .3s ease" }}>
            <span style={{ flex: "none", width: 8, height: 8, borderRadius: "50%", background: m.color, marginBottom: 17 }} />
            <span data-testid="syb-mode-label" style={{ flex: "none", fontSize: 9.5, letterSpacing: "0.2em", color: m.color, whiteSpace: "nowrap", marginBottom: 15 }}>{m.label}</span>
            <AutoTextarea data-testid="syb-input" className="syb-ph" value={p.draft} onChange={p.setDraft}
              onSend={() => { if (!sendDisabled) (asking ? p.onAskYuo() : p.onSend()); }}
              placeholder={`· ${toLabel}`} maxHeight={160}
              style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "var(--yuo-ink)", fontFamily: "inherit", fontSize: 13, padding: "11px 0" }} />
          </div>
          <button type="button" data-testid="syb-send" aria-label={asking ? "Ask YUO" : "send"} onClick={asking ? p.onAskYuo : p.onSend} disabled={sendDisabled} className="yuo-send-stone"
            style={{ flex: "none", width: 42, height: 42, borderRadius: "50%", border: "none", padding: 0, cursor: sendDisabled ? "default" : "pointer", opacity: sendDisabled ? 0.5 : 1,
              background: stoneOf("#3ddc97", "#0d3a28"), boxShadow: "0 0 20px rgba(61,220,151,0.4)" }} />
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
function RoomLoading() {
  return (
    <div style={{
      minHeight: "100vh", background: "var(--yuo-bg)", color: "var(--fg-4)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: '"JetBrains Mono", monospace', fontSize: 12, letterSpacing: "0.32em", textTransform: "uppercase",
    }}>opening the field…</div>
  );
}

function RoomError({ message, onBack }) {
  return (
    <div style={{
      minHeight: "100vh", background: "var(--yuo-bg)", color: "var(--yuo-ink)",
      display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20,
      fontFamily: '"JetBrains Mono", monospace',
    }}>
      <div style={{ color: "var(--lit-coral-14)", fontSize: 12, letterSpacing: "0.32em", textTransform: "uppercase" }}>
        {message}
      </div>
      <button onClick={onBack} style={{
        display: "inline-flex", alignItems: "center", gap: 10,
        background: "var(--fg-05)", border: "none",
        padding: "10px 22px", borderRadius: 999, cursor: "pointer",
        color: "var(--fg-75)", fontFamily: '"JetBrains Mono", monospace',
        fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase",
      }}>
        <span style={{
          width: 12, height: 12, borderRadius: "50%",
          background: "radial-gradient(34% 30% at 32% 26%, #fff, #8AFFC4 22%, #05060c 100%)",
          boxShadow: "0 0 10px rgba(138,255,196,0.7)",
        }} />
        back
      </button>
    </div>
  );
}
